import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./MobileOrderDashboard.css";
import MobileHeader from "./MobileHeader";
import MobileOrderTypeSelector from "./MobileOrderTypeSelector";
import MobileCategories from "./MobileCategories";
import MobileItemsList from "./MobileItemsList";
import MobileTableView from "./MobileTableView";
import MobileBottomNavigation from "./MobileBottomNavigation";
import MobileCart from "./MobileCart";
import MobileOrderSummaryBanner from "../components/MobileOrderSummaryBanner";
import TodaysReservationModal from "../../../components/common/Modals/TodaysReservationModal";
import { TYPESOFORDERS } from "../../../contants";
import { logoutUser } from "../../../redux/action/userActions";
import { findActiveOrderIndex } from "../../../redux/reducers/newOrderReducer";
import {
  getTableTypeFromOrder,
  getRoundOffType,
  getCompleteOrderTotals,
} from "../helpers/orderHelpers";
import calculateOrderTotals from "../../../helpers/calculateOrderTotals";
import roundOffTotal from "../../../helpers/roundOffTotal";
import useTodaysBookings from "../../../hooks/useTodaysBookings";
import {
  setDiscount,
  updateOrderDiscountSocket,
} from "../../../redux/action/orderActions";

const MobileOrderDashboard = ({
  handleKot,
  handleCustomerForm,
  handleItemClick,
  handleTableNumberClick,
  handleOtherOrderClick,
}) => {
  const [currentView, setCurrentView] = useState("home"); // home, tables, orders, cart
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const dispatch = useDispatch();

  const { cgst, sgst } = useSelector((state) => state.user);
  const bookings = useTodaysBookings();
  const {
    activeOrders,
    allItems,
    allTables,
    itemCategories,
    selectedOrderTypeId,
    activeOrder,
  } = useSelector((state) => state.order);
  const { tables } = useSelector((state) => state.branch);
  const { exclusiveGST, lowerRoundOff, upperRoundOff } = useSelector(
    (state) => state.util,
  );

  const orderIndex = findActiveOrderIndex(activeOrders, activeOrder);
  const activeOrderData =
    activeOrders && orderIndex !== null ? activeOrders[orderIndex] : null;

  // Get table type using shared helper
  const tableKind = getTableTypeFromOrder(tables, activeOrderData);

  // Get round-off type using shared helper
  const roundOffType = getRoundOffType(lowerRoundOff, upperRoundOff);

  // Calculate order totals using shared helper
  const orderTotals = getCompleteOrderTotals(
    activeOrderData,
    cgst,
    sgst,
    tableKind,
    exclusiveGST,
    roundOffType,
    calculateOrderTotals,
    roundOffTotal,
  );

  const { roundedGrandTotal: grandTotal } = orderTotals;

  React.useEffect(() => {
    if (
      activeOrderData &&
      activeOrderData.discountPercentage > 0 &&
      orderTotals.discount !== activeOrderData.discount
    ) {
      dispatch(
        setDiscount({
          discount: orderTotals.discount,
          discountPercentage: activeOrderData.discountPercentage,
        })
      );
      dispatch(
        updateOrderDiscountSocket(activeOrderData.refId, {
          discount: orderTotals.discount,
          discountPercentage: activeOrderData.discountPercentage,
        })
      );
    }
  }, [
    activeOrderData?.discountPercentage,
    orderTotals.discount,
    activeOrderData?.discount,
    activeOrderData?.refId,
    dispatch
  ]);

  const getFilteredItems = () => {
    let items = [...allItems]; // Copy the array before sorting

    // Filter by search
    if (searchQuery) {
      items = items.filter((item) =>
        item.itemName?.toLowerCase().includes(searchQuery?.toLowerCase()),
      );
    }

    // Filter by category
    if (selectedCategory !== "all") {
      items = items.filter((item) => item.categoryName === selectedCategory);
    }

    // Sort items by sequence (ascending). If sequence is undefined, null, or empty, default to Infinity so they go to the end.
    items.sort((a, b) => {
      const seqA =
        a.sequence === undefined || a.sequence === null || a.sequence === ""
          ? Infinity
          : Number(a.sequence);
      const seqB =
        b.sequence === undefined || b.sequence === null || b.sequence === ""
          ? Infinity
          : Number(b.sequence);
      if (seqA !== seqB) {
        return seqA - seqB;
      }
      return (a.itemName || "").localeCompare(b.itemName || "");
    });

    return items;
  };

  const getTotalCartItems = () => {
    if (
      !activeOrderData ||
      !activeOrderData.orderItems ||
      !Array.isArray(activeOrderData.orderItems)
    )
      return 0;
    return activeOrderData.orderItems.reduce(
      (total, item) => total + (item.quantity || 0),
      0,
    );
  };

  const handleTableSelect = (table) => {
    handleTableNumberClick(table);
    // Switch to home view after selecting table to add items
    setTimeout(() => setCurrentView("home"), 300);
  };

  const handleOrderSelect = (order) => {
    handleOtherOrderClick(order);
    // Switch to home view after selecting order
    setTimeout(() => setCurrentView("home"), 300);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
  };

  const renderContent = () => {
    switch (currentView) {
      case "tables":
        return (
          <MobileTableView
            tables={allTables}
            activeOrders={activeOrders}
            handleTableNumberClick={handleTableSelect}
            handleOtherOrderClick={handleOrderSelect}
            selectedOrderTypeId={selectedOrderTypeId}
          />
        );

      case "orders":
        return (
          <MobileTableView
            tables={allTables}
            activeOrders={activeOrders}
            handleTableNumberClick={handleTableSelect}
            handleOtherOrderClick={handleOrderSelect}
            selectedOrderTypeId={selectedOrderTypeId}
            showAllOrders={true}
          />
        );

      case "home":
      default:
        return (
          <>
            <MobileCategories
              categories={itemCategories}
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
            />
            <MobileItemsList
              items={getFilteredItems()}
              onItemClick={handleItemClick}
              activeOrder={activeOrderData}
            />
          </>
        );
    }
  };

  return (
    <div className="d-flex flex-column vh-100 bg-light overflow-hidden p-0 position-fixed mobile-dashboard">
      {/* Fixed Header */}
      <div className="position-sticky top-0 mobile-fixed-header">
        <MobileHeader
          onSearch={setSearchQuery}
          searchQuery={searchQuery}
          onLogout={handleLogout}
          bookingsCount={bookings?.length || 0}
          onBookingClick={() => setBookingModalOpen(true)}
        />

        <MobileOrderTypeSelector
          selectedType={selectedOrderTypeId}
          onSelectType={(typeId) => {
            // When switching to Parcel or Home Delivery, show the orders view
            // When switching to Dine In, show the tables view
            if (typeId === TYPESOFORDERS[0].value) {
              setCurrentView("tables");
            } else {
              setCurrentView("home");
            }
          }}
        />
      </div>

      {/* Active Order Banner - Using Shared Component */}
      <MobileOrderSummaryBanner
        activeOrder={activeOrderData}
        grandTotal={grandTotal}
      />

      {/* Scrollable Content */}
      <div className="flex-fill overflow-auto mobile-scroll-touch">
        {renderContent()}
      </div>

      {/* Bottom Navigation */}
      <MobileBottomNavigation
        currentView={currentView}
        onViewChange={setCurrentView}
        cartItemCount={getTotalCartItems()}
        onCartClick={() => setIsCartOpen(true)}
      />

      {/* Cart Drawer */}
      <MobileCart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        activeOrder={activeOrderData}
        handleKot={handleKot}
        handleCustomerForm={handleCustomerForm}
      />

      <TodaysReservationModal
        open={bookingModalOpen}
        onClose={() => setBookingModalOpen(false)}
      />
    </div>
  );
};

export default MobileOrderDashboard;
